import { GrainStorageTypeEnum } from '@/common/constants';
import { ComboboxControl } from '@/components/controls/combobox-control';
import { InputControl } from '@/components/controls/input-control';
import SelectControl from '@/components/controls/select-control';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import useStore from '@/lib/store';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateGrainReception } from '../../hooks/useCreateGrainReception';
import { PrintDocsModal } from './GenerateGrainDocsModal';

const contractTypeStyles: Record<string, string> = {
  STORAGE: 'bg-yellow-200 text-yellow-800',
  SEED: 'bg-red text-red-800',
  STATE_RESERVE: 'bg-[#93C5FD] text-blue-800',
};

export const GeneralGrainForm: FC = () => {
  const { t } = useTranslation();
  const { user } = useStore();
  const {
    form,
    load,
    contract,
    saveForm,
    plantsGrain,
    storageTypes,
    storageType,
    contractList,
    generateDocs,
    cropTypesByPlant,
    receptionGrainLoading,
    cropGenerationsByPlantId,
  } = useCreateGrainReception();

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(saveForm)}
        className={cn('flex flex-col items-end space-y-5')}
      >
        <div className="grid grid-cols-12 items-end gap-4 w-full">
          <InputControl
            control={form.control}
            name="tax_number"
            label={t('stir')}
            placeholder={t('enter.stir')}
            className="col-span-4"
            maxLength={9}
            isNumber
          />
          <InputControl
            control={form.control}
            name="farmer_name"
            label={t('name.product.delivery')}
            placeholder="---"
            className="col-span-4"
            disabled
          />
          <InputControl
            control={form.control}
            name="organization_name"
            label={t('receiving.organization.name')}
            placeholder="---"
            className="col-span-4"
            disabled
          />
          <SelectControl
            name="farmer_contract_id"
            label={t('contract')}
            options={contractList}
            control={form.control}
            placeholder={t('choose.data')}
            className={cn('col-span-4')}
            disabledItem={(option) => !option?.can_use}
            itemClassName={(option) =>
              cn(
                'transition-colors',
                contractTypeStyles[option?.contract_type] ??
                  'bg-gray-100 text-gray-800',
                '[&:not(:last-child)]:border-b border-gray-300',
              )
            }
          />
          <InputControl
            control={form.control}
            name="contract_amount"
            label={t('contract.amount')}
            placeholder="---"
            className="col-span-2"
            disabled
          />
          <InputControl
            control={form.control}
            name="reception_weight"
            label={t('reception.amount')}
            placeholder="---"
            className="col-span-2"
            disabled
          />
          <InputControl
            control={form.control}
            name="residual_amount"
            label={t('residual.amount')}
            placeholder="---"
            className="col-span-4"
            disabled
            isNumber
          />
          <SelectControl
            name="plant_id"
            label={t('type.of.crop')}
            options={plantsGrain}
            control={form.control}
            placeholder={t('choose.data')}
            className="col-span-4"
            disabled
          />
          <SelectControl
            name="storage_type"
            label={t('storage.type')}
            options={storageTypes}
            control={form.control}
            placeholder={t('choose.data')}
            className="col-span-4"
            disabled
          />
          <ComboboxControl
            name="crop_type_id"
            label={t('selection.type')}
            options={cropTypesByPlant}
            control={form.control}
            placeholder={t('choose.data')}
            className="w-full col-span-4"
          />
          {storageType === GrainStorageTypeEnum.SEED && (
            <SelectControl
              name="crop_generation_id"
              label={t('selection.type.clan')}
              options={cropGenerationsByPlantId}
              control={form.control}
              placeholder={t('choose.data')}
              className="col-span-4"
            />
          )}

          {/* <InputControl
            control={form.control}
            name="area"
            label={t('cultivated.area')}
            placeholder={t('enter.area')}
            className="col-span-4"
            isNumber
          /> */}
        </div>

        <div className="flex-h-center space-x-4">
          {user && (
            <PrintDocsModal
              printDocs={generateDocs}
              contract={contract}
              organization={user}
              id={load?.number}
              loadId={load?.id}
              farmer_tax_number={load?.farmer_tax_number}
              created_at={load?.created_at}
            >
              <Button
                type="submit"
                isLoading={receptionGrainLoading}
                disabled={!form.formState.isValid || receptionGrainLoading}
              >
                {t('next')}
                <ChevronRight size={16} color="white" />
              </Button>
            </PrintDocsModal>
          )}
        </div>
      </form>
    </Form>
  );
};
